import React, { memo, useState, useEffect } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import axios from 'axios'
import { bool, number, string, object, node, func } from 'prop-types'

import { getScrollbarWidth } from 'Pods/Shared/Utils/getScrollbarWidth'
import SvgNavbarIcon from 'Pods/UnifiedNavigation/Shared/NavbarIcon/NavbarIcon'

import { useActions } from './Hooks/useActions'

function UserAction({
  isHidden = false,
  id,
  isCurrentUser = false,
  href,
  icon,
  isForUser = false,
  notificationsCount = 0,
  popoverComponent = null,
  data = null,
  bottomElement = null,
  text = null,
  isPopoverLoaded = false,
  hasPopover = false,
  onWidgetLoad = null,
}) {
  const [currentCount, setCurrentCount] = useState(notificationsCount)

  useEffect(() => {
    setCurrentCount(notificationsCount)
  }, [notificationsCount])

  function markAllAsRead() {
    axios
      .put('/api/internal/users/check-notifications')
      .then(() => setCurrentCount(0))
      .catch()
  }

  const { activePopover, updatePopover: setActivePopover, wrapperRef, triggerRef } = useActions(id)

  useEffect(() => {
    if (id === 'cart-action' && activePopover === id) {
      document.body.classList.add('disable-page-scroll')

      const scrollbarWidth = getScrollbarWidth()
      if (scrollbarWidth > 0) {
        document.body.classList.add('scroll-disabled-padding')
      }
    } else {
      document.body.classList.remove('disable-page-scroll', 'scroll-disabled-padding')
    }

    return () => {
      document.body.classList.remove('disable-page-scroll', 'scroll-disabled-padding')
    }
  }, [id, activePopover])

  const dataAttributes = data
    ? {
        'data-track-label': data.trackLabel,
        'data-track-text': data.trackText,
        'data-avoid-navigation': data.avoidNavigation,
      }
    : {}

  function getAccessibleLabel() {
    const labels = {
      'cart-action': 'Cart',
      'inbox-action': 'Messages',
      'notifications-action': 'Notifications',
      'upload-action': 'Upload',
      'boost-credits-action': 'Boost Credits',
      'get-subscription-action': 'Get Subscription',
    }

    const baseLabel = labels[id] || undefined

    return baseLabel
  }

  function handleNotificationsAction() {
    if (id === 'notifications-action') {
      markAllAsRead()
    }
  }

  function handlePopover(e) {
    const popoverContainer = wrapperRef.current

    if (popoverContainer && popoverContainer.contains(e.target)) {
      return
    }

    if (id !== activePopover && hasPopover) {
      e.preventDefault()

      if (onWidgetLoad && !isPopoverLoaded) {
        onWidgetLoad(id, () => {
          setActivePopover(id)
          handleNotificationsAction()
        })
      } else {
        setActivePopover(id)
        handleNotificationsAction()
      }
    }

    if (id === activePopover) {
      e.preventDefault()
      setActivePopover(false)
    }
  }

  function handleMouseEnter() {
    if (onWidgetLoad && !isPopoverLoaded) {
      onWidgetLoad(id)
    }
  }

  function handleTouchStart() {
    if (onWidgetLoad && !isPopoverLoaded) {
      onWidgetLoad(id)
    }
  }

  if (!isCurrentUser && isForUser) {
    return null
  }

  if (isHidden) {
    return null
  }

  return (
    <li
      className={classnames(
        'wayfinder__action',
        id === 'upload-action' && 'wayfinder__action--direct',
        id === 'cart-action' && 'js-shopping-cart-icon',
        id === 'get-subscription-action' && 'wayfinder__action--subscription'
      )}
      onClick={handlePopover}
      onMouseEnter={hasPopover ? handleMouseEnter : undefined}
      onTouchStart={hasPopover ? handleTouchStart : undefined}
      ref={triggerRef}
      id={id}
    >
      <a className="wayfinder__action-link" href={href} aria-label={getAccessibleLabel()} {...dataAttributes}>
        {text && (
          <span className="wayfinder__action-text wayfinder__action-text--desktop-only">
            {text}
          </span>
        )}
        <span className="wayfinder__action-icon">
          <SvgNavbarIcon type={icon} className="wayfinder__action-icon-svg" />

          {!!currentCount && (
            <span className="cgt-badge cgt-badge--warning wayfinder__action-badge">
              <span className="cgt-badge__content wayfinder__action-count">{currentCount}</span>
            </span>
          )}

          {bottomElement}
        </span>
      </a>
      {activePopover === id && (
        <div
          className={classnames(
            'popover',
            'is-active',
            id === 'cart-action' && 'is-scrollable'
          )}
        >
          <div className="popover-container" ref={wrapperRef}>
            {popoverComponent}
          </div>
        </div>
      )}
    </li>
  )
}

UserAction.propTypes = {
  isHidden: bool,
  id: string.isRequired,
  isCurrentUser: bool,
  href: string.isRequired,
  icon: string.isRequired,
  isForUser: bool,
  notificationsCount: number,
  popoverComponent: node,
  bottomElement: node,
  data: object,
  text: string,
  onWidgetLoad: func,
  isPopoverLoaded: bool,
  hasPopover: bool,
}

export default memo(UserAction)
